import { workflowsApi } from '@/api/workflows';
import { notifySessionActivity } from '@/auth/sessionTimer';
import { useRoles } from '@/auth/useRoles';

/**
 * Uploads one advisor additional document and resolves with what the server stored.
 * `AdditionalDocumentsStep` receives this as an optional prop — see the note below on why
 * absence, rather than a boolean, is the guard.
 *
 * <p>
 * <b>Nothing BMO will see is decided here.</b> The §14.1 DOCNAME, obligation, disposition and
 * sequence used to travel on this call, because the delivery name was minted into the object's S3
 * key at upload. They are all composed at the deliver call now, from the description the advisor
 * has settled on by then — so this request carries bytes, and the entry id that decides whether
 * they get converted, and nothing else.
 * </p>
 */
export type AdditionalDocumentUploader = (args: {
  file: File;
  /**
   * The originator entry this file fills. Absent for the unnamed optional bucket, which has no
   * entry.
   *
   * The server looks the entry up from this id to read its `doNotParseToPdf` flag, which decides
   * whether the §14.1 delivery name is minted with the source extension or with `.pdf`
   * (ADR-0025 / ADR-0026). Deliberately an id and not the resolved flag: that name is what BMO
   * identifies, sequences and de-duplicates on, so the decision is not the client's to make.
   */
  entryId?: string;
}) => Promise<{ key: string; storedFileName: string }>;

/**
 * Last-resort DOCNAME for a bucket file the advisor named nothing and whose own filename is
 * unusable. It used to be the name EVERY bucket file was delivered under, which left BMO looking
 * at a step's worth of documents all called `SUPPORTINGDOC`.
 *
 * The server keeps its own copy (`FileXFileName.DefaultSupportingDocumentName`) because it has the
 * last word on the name — an advisor-typed name that folds away to nothing lands there too.
 */
export const OPTIONAL_BUCKET_DOCUMENT_NAME = 'SUPPORTINGDOC';

/**
 * How much of a bucket file's description — or, when there is none, of the uploaded file's own
 * name — reaches §14.1's DOCNAME. The step cuts it before sending and the server re-applies the
 * same cap (`MaxAdvisorDocumentNameLength`), because this name is what BMO identifies the document
 * by and a client is not trusted to cap it.
 *
 * <p>
 * 99 since 2026-11-04, up from 20, and the same figure as
 * {@link BUCKET_DOCUMENT_DESCRIPTION_MAX_LENGTH} and the server's
 * `FileXFileName.MaxDocumentNameLength`. The DOCNAME limit is a limit on the FIELD, so the
 * advisor's half of it has no reason to be shorter than the field it fills — and a description box
 * that took more than the name could carry was a promise the delivery could not keep. The three
 * numbers move together.
 * </p>
 *
 * <p>
 * No character allow-list goes with it, even though the DOCNAME comes from typed text. The same
 * text is ALSO delivered verbatim as `supportingDocs[].description`, so it has to stay free prose
 * — refusing the comma in "Trust deed, including every schedule" would be enforcing §14.1's field
 * grammar on a field §14.1 does not govern. The fold rewrites what it cannot carry
 * (`Relevé d'emploi` → `RELEV-D-EMPLOI`) and the description BMO reads is unaffected.
 * </p>
 */
export const BUCKET_DOCUMENT_NAME_MAX_LENGTH = 99;

/**
 * How long the description an advisor types beside an "Other documents" file may be.
 *
 * <p>
 * The full text is what the advisor and the reviewer see and what BMO receives as
 * `supportingDocs[].description` — delivered verbatim and folded by nothing. Only its opening
 * {@link BUCKET_DOCUMENT_NAME_MAX_LENGTH} characters have a second life as §14.1's DOCNAME, and
 * that is what closes the box once the upload starts: the name is minted into the object's S3 key
 * and never rebuilt.
 * </p>
 *
 * <p>
 * 99 — cut down from the 999 of QA D-44 (2026-11-04), and the same figure as
 * {@link BUCKET_DOCUMENT_NAME_MAX_LENGTH}. With the two equal, the whole description IS the
 * DOCNAME: nothing an advisor can type into the box is dropped on the way to BMO, which is the
 * point of matching them rather than leaving the name a window onto a longer field.
 * </p>
 */
export const BUCKET_DOCUMENT_DESCRIPTION_MAX_LENGTH = 99;

/**
 * Builds the uploader for the additional-documents step — or returns `undefined`, which is the
 * whole point of this hook.
 *
 * <h3>Why preview must not upload</h3>
 * `AdditionalDocumentsStep` renders in THREE places: advisor runtime, the originator's builder
 * preview, and the approver's review. Only the first is a real submission. If the step uploaded
 * unconditionally, an originator opening preview and dropping a test file would write it into the
 * customer-PII bucket (ADR-0019) — a bucket that exists precisely so that real customer documents
 * are held under a key no Paralucent human can decrypt, with a TTL and a malware gate. Preview
 * junk in there is PII-bucket pollution that nobody can inspect or clean up by eye, and it would
 * be scanned, tagged and billed like the real thing.
 *
 * <h3>Why absence rather than a flag</h3>
 * This returns `undefined` outside advisor runtime instead of an uploader that no-ops, so the
 * step has no upload path to reach at all in preview. A boolean would leave the call site one
 * inverted condition away from writing PII; there is nothing to invert here. The server also
 * gates the route on the advisor role as a backstop, so a regression here fails closed with a 403
 * rather than silently succeeding.
 */
export function useAdditionalDocumentUpload({
  templateId,
  subsequentTemplateId,
  mode,
  applicationId,
}: {
  templateId: string | undefined;
  /**
   * The workflow a form branch swapped in, if one fired; null otherwise.
   *
   * It does NOT change where the file is posted. `templateId` is minted into the object's S3 key,
   * and that key is what the file-status poll, the deliver call and submit's own backstop all
   * re-check ownership against — all three on the base form. Re-pointing the upload alone would
   * mint keys none of them recognise, and §14.1's APPTYPE would carry the target's appName rather
   * than the one BMO holds the application under.
   *
   * What it changes is which template the server resolves `entryId` against. The branch target
   * owns the additional-documents step and every entry on it (`composeBranchedTemplate` drops the
   * base form's with the rest of its steps below the branch point), so without this every named
   * entry was refused with "entryId does not match an additional-document entry on this workflow"
   * — DEV-SA-3, which left bulk subsequent applications unable to submit at all.
   */
  subsequentTemplateId: string | null | undefined;
  /** FormShell's mode — `'preview'` is the originator/approver rendering, never a real submission. */
  mode: 'preview' | 'advisor';
  /**
   * BMO's id for the in-progress application, returned by the first save call. It scopes the
   * object's S3 key, so uploading is impossible before it exists.
   */
  applicationId: string | undefined;
}): AdditionalDocumentUploader | undefined {
  const { role } = useRoles();

  // Both conditions, deliberately. `mode` is the structural signal and `role` is the identity
  // one; requiring both means neither a mis-passed mode nor a mis-resolved role is sufficient on
  // its own to start writing customer documents.
  const isAdvisorRuntime = mode === 'advisor' && role === 'Advisor';
  if (!isAdvisorRuntime || !templateId || !applicationId) {
    return undefined;
  }

  return async ({ file, entryId }) => {
    // An advisor mid-upload shouldn't cross the idle-refresh boundary (ADR-0022).
    notifySessionActivity();

    // One round trip through the API, which writes the bytes to the additional-files bucket and
    // returns the key plus the name the object was stored under (ADR-0028). The session cookie is
    // REQUIRED here — under the presigned design it was deliberately withheld, because the URL
    // carried its own auth and the cookie would have leaked to a different origin.
    //
    // Resolving does not mean the file is usable: it lands untagged and the bucket policy denies
    // reads until GuardDuty clears it. `useAdditionalDocumentScanStatus` polls for that verdict.
    const { key, storedFileName } = await workflowsApi.uploadAdditionalDocument(templateId, file, {
      applicationId,
      entryId,
      subsequentTemplateId,
    });

    return { key, storedFileName };
  };
}
